//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React, { createContext } from 'react';
import {
  type PanelBounds, type PanelRect, type PanelSize
} from '@/components/Panels/panelGeometry';
import { type PanelAnchor } from '@/services/apiTypes';

// -----------------------------------------------------------------------
// Panel context value -- the floating-panel registry.  Lives above the router
// so a panel survives navigation: re-parenting or unmounting a panel would
// reload the iframe a plugin surface renders into.
// -----------------------------------------------------------------------

/** Everything a client declares once, when it registers. */
export interface PanelSpec {
  // Stable across the session. `chat` for the built-in client; plugin panels
  // use `plugin:<name>:<surfaceKey>`.
  readonly id: string;
  readonly title: string;
  // Drawn beside the title and inside the panel's launcher, so the two read
  // as one object. Never part of an accessible name: it repeats the title it
  // sits next to. Optional, since a panel is identifiable without one.
  readonly icon?: React.ReactNode;
  // True when the icon above is the generic fallback rather than one this
  // panel chose -- a manifest that named no icon, or named one the host does
  // not have. Every such panel draws the same glyph, so it tells a reader
  // only that this is a plugin. The rail drops it and stands on the label;
  // a launcher that draws the name beside its glyph keeps both.
  readonly isIconGeneric?: boolean;
  readonly size: PanelSize;
  readonly minSize?: PanelSize;
  readonly anchor: PanelAnchor;
  readonly isResizable: boolean;
  // Whether a user may stop this panel at all. A panel is never closed from
  // its own chrome -- hiding is the only dismissal, and it discards nothing
  // -- so this gates the Tools list's Stop instead: false means releasing
  // the panel would discard something it exists to hold, and no control may
  // do that. Withdrawing the surface itself (unregisterPanel) still takes
  // the panel down, since there is nothing left to render it.
  readonly isClosable: boolean;
  // Whether the panel should be mounted parked shortly after sign-in rather
  // than waiting for a first open, for a surface that has to accumulate
  // state while nobody is looking at it. A remembered panel is restored
  // instead, so this never parks one the session left open.
  readonly preload?: boolean;
  // Whether this panel's launcher exists only on viewports wide enough for
  // the floating rail. The registry puts a panel on screen unasked in two
  // cases -- a session restore and a preload -- and skips both for a panel
  // declaring this on a narrow viewport, where it would iconify into a rail
  // that is not drawn and be unreachable. Plugin panels set it, since every
  // plugin-panel launcher lives on the desktop header; chat leaves it unset,
  // its pill being drawn at every width.
  readonly hasDesktopOnlyLauncher?: boolean;
  // The panel's contents. Called on every render of the host, so it must not
  // hold state of its own -- the body component does that.
  readonly render: () => React.ReactNode;
}

/** A panel that has been mounted this session. */
export interface PanelEntry {
  readonly spec: PanelSpec;
  readonly state: 'open' | 'iconified';
  readonly rect: PanelRect;
  // The panel's place in the stacking order: higher is nearer the front, and
  // every raise issues a fresh value. Stacking is a field rather than the
  // array's order so that raising a panel never reorders the array -- a
  // reorder moves the panel's DOM node, and a node moved mid-press silently
  // voids the pointer capture a drag on its title bar just took. Zero is
  // reserved for a panel mounted parked, which nothing has raised yet.
  readonly stackOrder: number;
  // Whether becoming visible should move focus into the panel. True for an
  // open the user asked for; false for one the session restored, which must
  // not drag focus into a portal at the end of the body on page load.
  readonly takesFocus: boolean;
  // Bumped on every open the user asks for. Opening a panel that is already
  // open changes neither `state` nor `takesFocus`, so this is the one thing
  // that says a new request happened and focus should move into the panel
  // again.
  readonly focusEpoch: number;
}

export interface PanelContextValue {
  // In mount order, which never changes while a panel stays mounted; the
  // stacking order is each entry's own `stackOrder`.
  panels: PanelEntry[];
  bounds: PanelBounds;
  setBounds: (bounds: PanelBounds) => void;
  // Idempotent: a client registers from wherever it lives, and re-registering
  // the same spec is the common case.
  registerPanel: (spec: PanelSpec) => void;
  // The counterpart, for a surface withdrawn mid-session -- an admin turning
  // it off, a plugin going away. It takes the panel down with the
  // registration on purpose: an entry left behind would keep a live iframe
  // of the withdrawn surface mounted with no launcher and no way to close it.
  unregisterPanel: (id: string) => void;
  // What a client declared, for a launcher that has to draw a panel it has
  // never opened. Registration is the only source: a launcher that reached
  // into `panels` could only describe a panel already mounted.
  getPanelSpec: (id: string) => PanelSpec | undefined;
  // What the user activated to open this panel, for handing focus back when
  // it is dismissed. Null when nothing was recorded -- an open with no
  // gesture behind it -- or when the element has since left the document.
  getPanelInvoker: (id: string) => HTMLElement | null;
  // Open a panel because the user asked for it: focus moves into it, and
  // whatever they activated is recorded to hand focus back to.
  openPanel: (id: string) => void;
  // Mount a panel parked rather than on screen, the way a preloading spec is
  // mounted. A no-op for a panel already mounted: it must not park, move, or
  // re-stack what a user is using.
  mountPanel: (id: string) => void;
  // Put a panel back the way the session left it, which is what the registry
  // does with a remembered id. Nothing asked for it, so it takes no focus and
  // records no invoker -- the separate verb is what keeps a caller from
  // getting the seizing behavior by omission.
  restorePanel: (id: string) => void;
  iconifyPanel: (id: string) => void;
  closePanel: (id: string) => void;
  raisePanel: (id: string) => void;
  movePanel: (id: string, x: number, y: number) => void;
  // A rect rather than a size: a resize from the north or west edge
  // moves the origin as well, and the two travel as one commit.
  resizePanel: (id: string, rect: PanelRect) => void;
  resetPanelGeometry: (id: string) => void;
  isPanelOpen: (id: string) => boolean;
  isPanelMounted: (id: string) => boolean;
}

const PanelContext = createContext<PanelContextValue | null>(null);

export { PanelContext };
